import React from "react";

import { APP_TOKENS as tokens } from "../../../../styles/tokens";
import {
  LAB_MATCHED_CSS_SIZE,
  LAB_PLACEMENT,
  runImageToolsRenderQualityLab,
  TRenderLabResult,
  TRenderLabVariant,
} from "./imageToolsRenderQualityLab";

type TInspectionMode = "matched" | "actual" | "nearest";

declare global {
  interface Window {
    __IMAGE_TOOLS_RENDER_QUALITY_LAB__?: {
      status: "loading" | "ready" | "error";
      result?: TRenderLabResult;
      error?: string;
    };
  }
}

const INSPECTION_MODES: Array<{
  id: TInspectionMode;
  label: string;
}> = [
  { id: "matched", label: "Preview matched" },
  { id: "actual", label: "100%" },
  { id: "nearest", label: "Nearest-neighbor" },
];

const LabButton: React.FC<{
  children: React.ReactNode;
  selected?: boolean;
  onClick: () => void;
}> = ({ children, selected = false, onClick }) => {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setPressed(false);
      }}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      style={{
        ...styles.button,
        ...(selected ? styles.buttonSelected : {}),
        ...(hovered ? styles.buttonHovered : {}),
        ...(focused ? styles.buttonFocused : {}),
        ...(pressed ? styles.buttonPressed : {}),
      }}
    >
      {children}
    </button>
  );
};

const formatMetric = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(4);

const getVariantImageStyle = (mode: TInspectionMode): React.CSSProperties => {
  if (mode === "actual") {
    return {
      width: "auto",
      height: "auto",
      maxWidth: "none",
      imageRendering: "auto",
    };
  }
  if (mode === "nearest") {
    return {
      width: LAB_MATCHED_CSS_SIZE.width * 2,
      height: LAB_MATCHED_CSS_SIZE.height * 2,
      maxWidth: "none",
      imageRendering: "pixelated",
    };
  }
  return {
    width: "100%",
    height: "auto",
    maxWidth: LAB_MATCHED_CSS_SIZE.width,
    imageRendering: "auto",
  };
};

const VariantCard: React.FC<{
  mode: TInspectionMode;
  variant: TRenderLabVariant;
}> = ({ mode, variant }) => (
  <article
    style={styles.card}
    data-testid={`render-quality-variant-${variant.id}`}
  >
    <div style={styles.cardHeading}>
      <div>
        <h2 style={styles.cardTitle}>{variant.label}</h2>
        <p style={styles.cardDescription}>{variant.description}</p>
      </div>
      <a
        href={variant.outputUrl}
        download={`image-tools-render-quality-${variant.id}.png`}
        style={styles.downloadLink}
      >
        Download PNG
      </a>
    </div>
    <div style={styles.imageViewport}>
      <img
        src={mode === "matched" ? variant.matchedUrl : variant.outputUrl}
        alt={`${variant.label} deterministic render`}
        style={getVariantImageStyle(mode)}
      />
    </div>
    <dl style={styles.metrics}>
      <div>
        <dt>Mean RGBA error</dt>
        <dd>{formatMetric(variant.metrics.meanAbsoluteRgbaDifference)}</dd>
      </div>
      <div>
        <dt>SSIM</dt>
        <dd>{formatMetric(variant.metrics.ssim)}</dd>
      </div>
      <div>
        <dt>Edge energy ratio</dt>
        <dd>{formatMetric(variant.metrics.edgeEnergyRatio)}</dd>
      </div>
      <div>
        <dt>Edge spread</dt>
        <dd>{variant.metrics.edgeSpreadPixels} px</dd>
      </div>
      <div>
        <dt>Encode/decode max</dt>
        <dd>{variant.encodeDecode.maximumRgbaDifference}</dd>
      </div>
      <div>
        <dt>Output rectangle</dt>
        <dd>
          {[
            variant.outputRectangle.x,
            variant.outputRectangle.y,
            variant.outputRectangle.width,
            variant.outputRectangle.height,
          ]
            .map(formatMetric)
            .join(", ")}
        </dd>
      </div>
    </dl>
  </article>
);

const DomReference: React.FC<{ stickerUrl: string }> = ({ stickerUrl }) => (
  <article style={styles.card} data-testid="render-quality-dom-reference">
    <div style={styles.cardHeading}>
      <div>
        <h2 style={styles.cardTitle}>Live DOM reference</h2>
        <p style={styles.cardDescription}>
          Separate background and sticker images composed by browser layout at
          the final matched display size.
        </p>
      </div>
    </div>
    <div style={styles.imageViewport}>
      <div
        style={styles.domStage}
        data-testid="render-quality-dom-stage"
        aria-label="Live DOM sticker reference"
      >
        <img
          src="/render-quality-fixtures/background.svg"
          alt=""
          style={styles.domBackground}
        />
        <img
          src={stickerUrl}
          alt="Deterministic test sticker"
          style={{
            ...styles.domSticker,
            left: `${LAB_PLACEMENT.x * 100}%`,
            top: `${LAB_PLACEMENT.y * 100}%`,
            width: `${LAB_PLACEMENT.width * 100}%`,
            height: `${LAB_PLACEMENT.height * 100}%`,
          }}
        />
      </div>
    </div>
  </article>
);

const ImageToolsRenderQualityLabPage: React.FC = () => {
  const [mode, setMode] = React.useState<TInspectionMode>("matched");
  const [result, setResult] = React.useState<TRenderLabResult | null>(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let active = true;
    window.__IMAGE_TOOLS_RENDER_QUALITY_LAB__ = { status: "loading" };
    runImageToolsRenderQualityLab()
      .then((nextResult) => {
        if (!active) return;
        setResult(nextResult);
        window.__IMAGE_TOOLS_RENDER_QUALITY_LAB__ = {
          status: "ready",
          result: nextResult,
        };
      })
      .catch((caughtError) => {
        if (!active) return;
        const message =
          caughtError instanceof Error
            ? caughtError.message
            : "The render-quality experiment could not run.";
        setError(message);
        window.__IMAGE_TOOLS_RENDER_QUALITY_LAB__ = {
          status: "error",
          error: message,
        };
      });
    return () => {
      active = false;
    };
  }, []);

  if (process.env.EXPO_PUBLIC_ENV === "production") {
    return (
      <main style={styles.page}>
        <section style={styles.content}>
          <h1 style={styles.title}>Render-quality lab unavailable</h1>
          <p style={styles.introduction}>
            This deterministic diagnostic route is disabled in production.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main style={styles.page}>
      <section style={styles.content}>
        <header style={styles.header}>
          <p style={styles.eyebrow}>DEVELOPMENT DIAGNOSTIC</p>
          <h1 style={styles.title}>Image Tools sticker render-quality lab</h1>
          <p style={styles.introduction}>
            Fixed, non-clinical fixtures isolate Canvas smoothing, fractional
            geometry, source resolution, alpha edges, and PNG encode/decode.
            Every candidate changes one variable from the current renderer.
          </p>
        </header>

        <div role="group" aria-label="Inspection scale" style={styles.controls}>
          {INSPECTION_MODES.map((inspectionMode) => (
            <LabButton
              key={inspectionMode.id}
              selected={mode === inspectionMode.id}
              onClick={() => setMode(inspectionMode.id)}
            >
              {inspectionMode.label}
            </LabButton>
          ))}
        </div>

        {error ? (
          <div role="alert" style={styles.error}>
            <h2 style={styles.errorTitle}>Experiment failed</h2>
            <p style={styles.errorCopy}>{error}</p>
          </div>
        ) : null}

        {!result && !error ? (
          <div role="status" aria-live="polite" style={styles.loading}>
            Running deterministic pixel experiments…
          </div>
        ) : null}

        {result ? (
          <>
            <section style={styles.manifest} aria-label="Render manifest">
              <h2 style={styles.sectionTitle}>Manifest</h2>
              <dl style={styles.metrics}>
                <div>
                  <dt>Browser</dt>
                  <dd>{result.userAgent}</dd>
                </div>
                <div>
                  <dt>DPR</dt>
                  <dd>{result.devicePixelRatio}</dd>
                </div>
                <div>
                  <dt>Canvas</dt>
                  <dd>
                    {result.canvasSize.width} × {result.canvasSize.height}
                  </dd>
                </div>
                <div>
                  <dt>Matched display</dt>
                  <dd>
                    {result.matchedSize.cssWidth} ×{" "}
                    {result.matchedSize.cssHeight} CSS /{" "}
                    {result.matchedSize.pixelWidth} ×{" "}
                    {result.matchedSize.pixelHeight} px
                  </dd>
                </div>
                <div>
                  <dt>Canvas defaults</dt>
                  <dd>
                    smoothing{" "}
                    {String(
                      result.defaultContextSettings.imageSmoothingEnabled,
                    )}
                    , {result.defaultContextSettings.imageSmoothingQuality}
                  </dd>
                </div>
                <div>
                  <dt>Normalized placement</dt>
                  <dd>
                    {Object.values(result.placement)
                      .map(formatMetric)
                      .join(", ")}
                  </dd>
                </div>
              </dl>
            </section>

            <section style={styles.grid} aria-label="Renderer comparisons">
              <DomReference stickerUrl={result.referenceStickerUrl} />
              {result.variants.map((variant) => (
                <VariantCard key={variant.id} mode={mode} variant={variant} />
              ))}
            </section>

            <details style={styles.rawData}>
              <summary style={styles.summary}>
                Machine-readable manifest
              </summary>
              <pre style={styles.pre}>{JSON.stringify(result, null, 2)}</pre>
            </details>
          </>
        ) : null}
      </section>
    </main>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: tokens.color.background,
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.regular,
  },
  content: {
    width: "100%",
    maxWidth: 1280,
    margin: "0 auto",
    padding: `${tokens.spacing.xxxl}px ${tokens.spacing.xl}px ${tokens.spacing.giant}px`,
    boxSizing: "border-box",
  },
  header: {
    maxWidth: 880,
    marginBottom: tokens.spacing.xxl,
  },
  eyebrow: {
    margin: `0 0 ${tokens.spacing.sm}px`,
    color: tokens.color.accentDark,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
    letterSpacing: tokens.letterSpacing.caps,
  },
  title: {
    margin: 0,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.xxxl,
    lineHeight: `${tokens.lineHeight.xxxl}px`,
  },
  introduction: {
    margin: `${tokens.spacing.md}px 0 0`,
    color: tokens.color.textSecondary,
    fontSize: tokens.fontSize.md,
    lineHeight: `${tokens.lineHeight.lg}px`,
  },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacing.sm,
    marginBottom: tokens.spacing.xxl,
  },
  button: {
    minWidth: 44,
    minHeight: 44,
    padding: `${tokens.spacing.sm}px ${tokens.spacing.lg}px`,
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.borderStrong}`,
    borderRadius: tokens.radius.button,
    backgroundColor: tokens.color.surface,
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
    lineHeight: `${tokens.lineHeight.sm}px`,
    cursor: "pointer",
  },
  buttonSelected: {
    borderColor: tokens.color.accentDark,
    backgroundColor: tokens.color.accentMuted,
  },
  buttonHovered: {
    borderColor: tokens.color.accentDark,
  },
  buttonFocused: {
    outline: `${tokens.borderWidth.strong}px solid ${tokens.color.accentDark}`,
    outlineOffset: tokens.borderWidth.strong,
  },
  buttonPressed: {
    opacity: tokens.opacity.pressed,
  },
  loading: {
    padding: tokens.spacing.xxl,
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.surface,
    color: tokens.color.textSecondary,
  },
  error: {
    padding: tokens.spacing.xl,
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.errorBorder}`,
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.errorSurface,
    color: tokens.color.errorText,
  },
  errorTitle: {
    margin: 0,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.lg,
    lineHeight: `${tokens.lineHeight.lg}px`,
  },
  errorCopy: {
    margin: `${tokens.spacing.sm}px 0 0`,
  },
  manifest: {
    marginBottom: tokens.spacing.xxl,
    padding: tokens.spacing.xl,
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.surface,
  },
  sectionTitle: {
    margin: `0 0 ${tokens.spacing.md}px`,
    fontFamily: tokens.font.bold,
    fontSize: tokens.fontSize.xl,
    lineHeight: `${tokens.lineHeight.xl}px`,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
    gap: tokens.spacing.xl,
  },
  card: {
    minWidth: 0,
    padding: tokens.spacing.lg,
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.surface,
    boxShadow: `${tokens.shadow.card.shadowOffset.width}px ${tokens.shadow.card.shadowOffset.height}px ${tokens.shadow.card.shadowRadius}px rgba(30, 37, 51, ${tokens.shadow.card.shadowOpacity})`,
  },
  cardHeading: {
    minHeight: 96,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: tokens.spacing.md,
  },
  cardTitle: {
    margin: 0,
    fontFamily: tokens.font.semibold,
    fontSize: tokens.fontSize.lg,
    lineHeight: `${tokens.lineHeight.lg}px`,
  },
  cardDescription: {
    margin: `${tokens.spacing.xs}px 0 0`,
    color: tokens.color.textSecondary,
    fontSize: tokens.fontSize.sm,
    lineHeight: `${tokens.lineHeight.sm}px`,
  },
  downloadLink: {
    minHeight: 44,
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    color: tokens.color.accentDark,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
  },
  imageViewport: {
    width: "100%",
    maxHeight: 640,
    overflow: "auto",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.borderStrong}`,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.surfaceMuted,
  },
  domStage: {
    width: "100%",
    maxWidth: LAB_MATCHED_CSS_SIZE.width,
    aspectRatio: `${LAB_MATCHED_CSS_SIZE.width} / ${LAB_MATCHED_CSS_SIZE.height}`,
    position: "relative",
    overflow: "hidden",
  },
  domBackground: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "fill",
  },
  domSticker: {
    position: "absolute",
    objectFit: "fill",
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))",
    gap: tokens.spacing.md,
    margin: `${tokens.spacing.lg}px 0 0`,
  },
  rawData: {
    marginTop: tokens.spacing.xxl,
    padding: tokens.spacing.lg,
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.surface,
  },
  summary: {
    minHeight: 44,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    fontFamily: tokens.font.semibold,
  },
  pre: {
    maxHeight: 640,
    overflow: "auto",
    padding: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.surfaceMuted,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
};

export default ImageToolsRenderQualityLabPage;
